import React, { FunctionComponent, useState } from 'react'
import { Col, Container, Row } from 'react-grid-system'
import { matchPath, Route, useHistory, useLocation } from 'react-router'

import { ClinTheme } from '../../ClinTheme'
import { useAppContext } from '../../context/app/AppContext'
import { createAnnounceEvent } from '../../events/AnnounceEvent'
import { MaOrderDetailContainer } from '../../pages/MaOrderDetail'
import { MaOrderDetailContainerOld } from '../../pages/MaOrderDetail/old/MaOrderDetailContainerOld'
import { IMaOrderDetailProps } from '../../pages/MaOrderDetail/MaOrderDetailContainer'
import { OrderDetailContainer } from '../../pages/OrderPages/OrderDetail'
import { OrderDetailContainerOld } from '../../pages/OrderPages/OrderDetail/old/OrderDetailContainerOld'
import { IOrderDetailProps } from '../../pages/OrderPages/OrderDetail/OrderDetailContainer'
import analyticsServiceSingleton from '../../services/Analytics/initAnalytics'
import { cancelGetOrderById, getOrderById } from '../../services/ApiService'
import { OrderDto } from '../../types/swaggerTypes'
import { useErrorMessage } from '../../utils/useErrorMessage'
import { useOnMount } from '../../utils/useOnMount'
import { AnnounceMode } from '../ClinAnnounceBar/ClinAnnounceBar'
import { ClinSpinner } from '../ClinSpinner'
import { StyledSpinnerContainer } from '../ClinSpinner/ClinSpinner.styles'
import { FeatureFlagKeys } from '../../context/featureFlags/FeatureFlagKeys'
import { useFeatureFlags } from '../../context/featureFlags/FeatureFlagContext'

interface IOrderRouteParams {
  orderId: string
}

interface IOrderRouteProps {
  exact: boolean
  path: string
  countryCodesExcluded?: string[] //Country codes to be forbiden to access orders dashboard
}

const isMaOrder = (order: OrderDto): boolean =>
  order.lines.find((line) => line.division === 'MA') !== undefined

export const OrderRoute: FunctionComponent<IOrderRouteProps> = ({
  path,
  exact,
  countryCodesExcluded
}) => {
  const { portfolioCountryCode, dispatch } = useAppContext()
  const location = useLocation()
  const history = useHistory()
  const [order, setOrder] = useState<OrderDto>()
  const match = matchPath<IOrderRouteParams>(location.pathname, {
    path: path,
    exact: exact
  })

  const { useFeatureFlag } = useFeatureFlags()
  const enableNewOrderDetailsPage = useFeatureFlag(
    FeatureFlagKeys.NewOrderDetailsPage
  )

  if (countryCodesExcluded?.includes(portfolioCountryCode)) {
    window.location.href = '/products/catalogue'
  }
  const orderId = match?.params.orderId

  const handleError = useErrorMessage('There was an error fetching order.')
  useOnMount(() => {
    orderId &&
      getOrderById(orderId)
        .then((response) => {
          setOrder(response.data)
        })
        .catch((error) => {
          // Not found also covers an order the user has no access to: leave
          // the page rather than spin forever.
          if (error?.response?.status === 404) {
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Error,
                'This order could not be found, or you do not have access to it.'
              )
            )
            history.replace('/orders')
            return
          }
          analyticsServiceSingleton.trackError(
            error,
            JSON.parse(localStorage.getItem('current_user') || '{}')
          )
          handleError(error)
        })
    return () => {
      cancelGetOrderById()
    }
  })

  return order === undefined ? (
    <Container>
      <Row justify="center">
        <Col width="auto">
          <StyledSpinnerContainer>
            <ClinSpinner size={ClinTheme.space[7]} />
          </StyledSpinnerContainer>
        </Col>
      </Row>
    </Container>
  ) : (
    <Route
      path={path}
      exact={exact}
      render={(props) => {
        const updatedProps = props as IMaOrderDetailProps | IOrderDetailProps
        updatedProps.location.state = {
          ...updatedProps.location.state,
          orderDto: order
        }
        return isMaOrder(order) ? (
          <>
            {enableNewOrderDetailsPage ? (
              <MaOrderDetailContainer {...updatedProps} />
            ) : (
              <MaOrderDetailContainerOld {...updatedProps} />
            )}
          </>
        ) : (
          <>
            {enableNewOrderDetailsPage ? (
              <OrderDetailContainer {...updatedProps} />
            ) : (
              <OrderDetailContainerOld {...updatedProps} />
            )}
          </>
        )
      }}
    />
  )
}
